/**
 * Object-graph layer — draws the fix family (slice B) and the DR/EP family
 * (slice C) from the PlotObject graph: lines of position, range arcs, fixes,
 * cocked hats, water tracks, tidal vectors, ground tracks, and the DR and EP
 * marks.
 *
 * Kept separate from the older chartwork layer so the two run side by side
 * during the migration. Same source-and-layers pattern: one GeoJSON source for
 * geometry, one for labels, updated in place. Arrow conventions follow paper
 * chartwork: water track one arrow, tide three, ground track two.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import { objectsToGeoJSON } from '@/chartwork';
import type { PlotObject } from '@/chartwork';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'objects';
const LABEL_SOURCE_ID = 'objects-labels';

function buildSources(objects: PlotObject[]): {
  features: FeatureCollection;
  labels: FeatureCollection;
} {
  const { features, labels } = objectsToGeoJSON(objects);
  const labelFc: FeatureCollection = {
    type: 'FeatureCollection',
    features: labels.map((l) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: l.position },
      properties: { text: l.text },
    })),
  };
  return { features, labels: labelFc };
}

export function ensureObjectsLayer(map: MaplibreMap, objects: PlotObject[], palette: Palette): void {
  const p = getPalette(palette);
  const { features, labels } = buildSources(objects);

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: features });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(features);
  }
  if (!map.getSource(LABEL_SOURCE_ID)) {
    map.addSource(LABEL_SOURCE_ID, { type: 'geojson', data: labels });
  } else {
    (map.getSource(LABEL_SOURCE_ID) as maplibregl.GeoJSONSource).setData(labels);
  }

  const arrow = (id: string, role: string, field: string, size: number) => ({
    id,
    type: 'symbol',
    filter: ['==', ['get', 'role'], role],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 200,
      'text-field': field,
      'text-font': ['Open Sans Regular'],
      'text-size': size,
      'text-rotation-alignment': 'map',
      'text-keep-upright': false,
      'text-allow-overlap': true,
    },
    paint: { 'text-color': p.CHBLK, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
  });

  const layers = [
    {
      id: 'obj-cocked-hat',
      type: 'fill',
      filter: ['==', ['get', 'role'], 'cocked-hat'],
      paint: { 'fill-color': p.CHMGD, 'fill-opacity': 0.18, 'fill-outline-color': p.CHMGD },
    },
    {
      id: 'obj-lop',
      type: 'line',
      filter: ['==', ['get', 'role'], 'lop'],
      paint: { 'line-color': p.CHBLK, 'line-width': 1, 'line-opacity': 0.9 },
    },
    {
      id: 'obj-transferred',
      type: 'line',
      filter: ['==', ['get', 'role'], 'transferred'],
      paint: { 'line-color': p.CHBLK, 'line-width': 1.1, 'line-dasharray': [4, 2] },
    },
    {
      id: 'obj-range-circle',
      type: 'line',
      filter: ['==', ['get', 'role'], 'range-circle'],
      paint: { 'line-color': p.CHBLK, 'line-width': 1, 'line-opacity': 0.9 },
    },
    {
      id: 'obj-ground-track',
      type: 'line',
      filter: ['==', ['get', 'role'], 'ground-track'],
      paint: { 'line-color': p.CHBLK, 'line-width': 2.4 },
    },
    {
      id: 'obj-water-track',
      type: 'line',
      filter: ['==', ['get', 'role'], 'water-track'],
      paint: { 'line-color': p.CHBLK, 'line-width': 1.6 },
    },
    {
      id: 'obj-tide',
      type: 'line',
      filter: ['==', ['get', 'role'], 'tide'],
      paint: { 'line-color': p.CHBLK, 'line-width': 1.4, 'line-dasharray': [2, 1.5] },
    },
    {
      id: 'obj-set-drift',
      type: 'line',
      filter: ['==', ['get', 'role'], 'set-drift'],
      paint: { 'line-color': p.CHMGD, 'line-width': 1.6, 'line-dasharray': [3, 2] },
    },
    arrow('obj-setdrift-arrows', 'set-drift', '>>', 14),
    arrow('obj-water-arrows', 'water-track', '>', 16),
    arrow('obj-tide-arrows', 'tide', '>>>', 14),
    arrow('obj-ground-arrows', 'ground-track', '>>', 16),
    {
      id: 'obj-point-mark',
      type: 'circle',
      filter: ['==', ['get', 'role'], 'point-mark'],
      paint: {
        'circle-radius': 4,
        'circle-color': p.CHBLK,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 1.4,
      },
    },
    {
      id: 'obj-fix-mark',
      type: 'symbol',
      filter: ['==', ['get', 'role'], 'fix-mark'],
      layout: {
        'icon-image': 'plot-fix',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    {
      id: 'obj-dr-mark',
      type: 'symbol',
      filter: ['==', ['get', 'role'], 'dr-mark'],
      layout: {
        'icon-image': 'plot-dr',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    {
      id: 'obj-ep-mark',
      type: 'symbol',
      filter: ['==', ['get', 'role'], 'ep-mark'],
      layout: {
        'icon-image': 'plot-ep',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
  ];

  for (const l of layers) {
    if (!map.getLayer(l.id)) {
      map.addLayer({ ...l, source: SOURCE_ID } as unknown as maplibregl.LayerSpecification);
    }
  }

  if (!map.getLayer('obj-label')) {
    map.addLayer({
      id: 'obj-label',
      type: 'symbol',
      source: LABEL_SOURCE_ID,
      layout: {
        'text-field': ['get', 'text'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0.8, 0],
        'text-anchor': 'left',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': p.CHBLK,
        'text-halo-color': p.UI_BG,
        'text-halo-width': 1.6,
      },
    });
  }
}

export function updateObjectsLayer(map: MaplibreMap, objects: PlotObject[]): void {
  const { features, labels } = buildSources(objects);
  const src = map.getSource(SOURCE_ID);
  const lsrc = map.getSource(LABEL_SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(features);
  if (lsrc) (lsrc as maplibregl.GeoJSONSource).setData(labels);
}
